import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Database, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false,
    crm_url: "",
    api_key: "",
    auto_push: false,
    configured: false,
    api_key_masked: "",
    last_test_at: "",
    last_test_ok: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/crm");
      setCfg((c) => ({ ...c, ...data, api_key: "" })); // never expose real key
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/settings/crm", {
        enabled: cfg.enabled,
        crm_url: cfg.crm_url,
        api_key: cfg.api_key, // empty string => keep existing
        auto_push: cfg.auto_push,
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/crm/test");
      if (data.ok) toast.success(`Connection OK · HTTP ${data.status}`);
      else toast.error(`CRM responded ${data.status}: ${data.message || "—"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  const setupSnippet = `# 1) Add to your CRM's backend/.env:
LEAD_INGEST_API_KEY="2X7RerDD5QbFN-EvNOzYTMu1AOox5Tfa2W4ErTwsC9Y"

# 2) Add this to your CRM's backend (e.g. server.py):

from fastapi import APIRouter, Header, HTTPException, Depends, UploadFile, File, Response
from typing import Optional
from datetime import datetime, timezone
import os, uuid, base64

ingest_router = APIRouter(prefix="/api/ingest", tags=["lead-ingest"])

def verify_ingest_key(x_api_key: Optional[str] = Header(None)):
    if x_api_key != os.environ.get("LEAD_INGEST_API_KEY"):
        raise HTTPException(status_code=401, detail="Invalid API key")

@ingest_router.post("/file", dependencies=[Depends(verify_ingest_key)])
async def ingest_file(file: UploadFile = File(...)):
    content = await file.read()
    doc = {
        "id": str(uuid.uuid4()),
        "filename": file.filename,
        "content_b64": base64.b64encode(content).decode(),
        "size_bytes": len(content),
        "received_at": datetime.now(timezone.utc).isoformat(),
    }
    await db.ingested_files.insert_one(doc)  # adjust collection name if you wish
    return {"ok": True, "id": doc["id"], "filename": file.filename, "size_bytes": doc["size_bytes"]}

# Optional: list + download for your manager UI (protect with your own auth)
@ingest_router.get("/files")
async def list_ingested_files():
    cursor = db.ingested_files.find({}, {"_id": 0, "content_b64": 0}).sort("received_at", -1)
    return {"files": await cursor.to_list(length=500)}

@ingest_router.get("/files/{fid}/download")
async def download_ingested_file(fid: str):
    doc = await db.ingested_files.find_one({"id": fid})
    if not doc:
        raise HTTPException(404, "File not found")
    return Response(
        content=base64.b64decode(doc["content_b64"]),
        media_type="text/csv",
        headers={"Content-Disposition": f'attachment; filename=\"{doc["filename"]}\"'},
    )

app.include_router(ingest_router)

# 3) IMPORTANT — your CRM has IP restriction middleware.
#    Make it bypass /api/ingest/* (this route uses API-key auth).
#    Example exemption inside the middleware:
#        if request.url.path.startswith("/api/ingest"):
#            return await call_next(request)

# 4) Restart your CRM backend.

# What your CRM receives (multipart/form-data):
#   POST /api/ingest/file
#   Header:    X-API-Key: <LEAD_INGEST_API_KEY>
#   Form file: 'file' = <renamed>.csv with columns:
#              phone, email, first_name, last_name, street, city, state, zip`;

  return (
    <div className="p-6 md:p-10 max-w-[1100px]" data-testid="settings-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Configuration</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Settings</h1>
        <p className="text-gray-600 mt-2">Connect this Lead Portal to your CRM and other downstream systems.</p>
      </div>

      <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white" data-testid="crm-settings-card">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
              <Database size={18} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-gray-950">CRM Integration</div>
              <div className="text-sm text-gray-600 mt-0.5">Push leads to your CRM automatically or on demand.</div>
            </div>
          </div>
          {cfg.configured && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 rounded-sm" data-testid="crm-configured-badge">
              <CheckCircle2 size={12} /> Configured
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <label className="flex items-start gap-2 text-sm" data-testid="crm-enabled-toggle">
              <Checkbox checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })} className="mt-0.5" />
              <span>
                <span className="font-medium text-gray-900">Enable CRM integration</span>
                <span className="block text-xs text-gray-500 mt-0.5">When disabled, manual & auto-push are blocked.</span>
              </span>
            </label>

            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">CRM ingestion endpoint URL</Label>
              <Input
                value={cfg.crm_url}
                onChange={(e) => setCfg({ ...cfg, crm_url: e.target.value })}
                placeholder="https://access.omnilinksolutions.net/api/ingest/file"
                className="mt-1 h-10 rounded-sm font-mono text-xs"
                data-testid="crm-url-input"
              />
              <div className="text-xs text-gray-500 mt-1">Full URL to your CRM's lead-ingest endpoint.</div>
            </div>

            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">API Key</Label>
              <div className="relative mt-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={cfg.api_key}
                  onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })}
                  placeholder={cfg.api_key_masked || "Paste the shared secret here"}
                  className="h-10 rounded-sm font-mono text-xs pr-10"
                  data-testid="crm-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-700"
                  aria-label="toggle key visibility"
                >
                  {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {cfg.api_key_masked ? `Current: ${cfg.api_key_masked} (leave blank to keep)` : "This is the same key your CRM expects in X-API-Key header."}
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm" data-testid="crm-autopush-toggle">
              <Checkbox checked={cfg.auto_push} onCheckedChange={(v) => setCfg({ ...cfg, auto_push: !!v })} className="mt-0.5" />
              <span>
                <span className="font-medium text-gray-900">Auto-push after every upload</span>
                <span className="block text-xs text-gray-500 mt-0.5">As soon as a new file is processed, push its NEW (non-duplicate) leads to the CRM.</span>
              </span>
            </label>

            <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-100">
              <Button onClick={save} disabled={saving} className="rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="crm-save-btn">
                {saving ? "Saving…" : "Save settings"}
              </Button>
              <Button onClick={test} disabled={testing || !cfg.configured} variant="outline" className="rounded-sm" data-testid="crm-test-btn">
                {testing ? "Testing…" : "Test connection"}
              </Button>
              {cfg.last_test_at && (
                <span className="text-xs text-gray-600 self-center flex items-center gap-1.5" data-testid="crm-test-status" title={cfg.last_test_message || ""}>
                  {cfg.last_test_ok ? <CheckCircle2 size={14} className="text-[#00C04B]" /> : <AlertTriangle size={14} className="text-[#FF3B30]" />}
                  Last test: {new Date(cfg.last_test_at).toLocaleString()} · {cfg.last_test_ok ? "OK" : "Failed"}
                </span>
              )}
            </div>
            {cfg.last_test_at && !cfg.last_test_ok && cfg.last_test_message && (
              <div className="mt-2 text-xs text-[#FF3B30] font-mono bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-sm p-2.5 break-all" data-testid="crm-test-error">
                {cfg.last_test_message}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white mt-6" data-testid="crm-setup-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-display text-lg font-semibold text-gray-950">CRM-side setup</div>
            <div className="text-sm text-gray-600 mt-0.5">Paste this into your CRM's Emergent project. One-time setup.</div>
          </div>
          <a
            href="https://access.omnilinksolutions.net/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1"
          >
            Open CRM <ExternalLink size={12} />
          </a>
        </div>
        <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-sm p-4 overflow-x-auto whitespace-pre" data-testid="crm-setup-code">{setupSnippet}</pre>
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm mt-3"
          onClick={() => { navigator.clipboard.writeText(setupSnippet); toast.success("Setup snippet copied"); }}
          data-testid="copy-setup-btn"
        >
          Copy snippet
        </Button>
      </Card>
    </div>
  );
}
